import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, LogOut, ShieldAlert } from 'lucide-react';
import { UserRole } from '@weloze/shared';
import { useAuth } from '../lib/auth';
import { APP_NAME, PageLabelContext, useDocumentTitle } from '../lib/navigation';
import { BrandMark, Wordmark } from './Brand';
import { Breadcrumbs } from './Breadcrumbs';
import { SectionNav } from './SectionNav';
import { Button, cn } from './ui';

/**
 * Moldura das telas autenticadas: marca, seletor de loja e sair.
 *
 * O seletor só aparece quando o usuário tem mais de uma loja — quem tem uma só
 * não precisa escolher nada (PLAN §7, tela 2).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [pageLabel, setPageLabel] = useState<string | null>(null);
  const labelContext = useMemo(
    () => ({ label: pageLabel, setLabel: setPageLabel }),
    [pageLabel],
  );

  return (
    <PageLabelContext.Provider value={labelContext}>
      <Shell>{children}</Shell>
    </PageLabelContext.Provider>
  );
}

/**
 * O conteúdo da moldura vive dentro do provider porque a trilha e o título da
 * aba leem o rótulo que a tela publica — um hook chamado no próprio `AppShell`
 * enxergaria o contexto de fora, ou seja, sempre vazio.
 */
function Shell({ children }: { children: ReactNode }) {
  const { user, currentStore, setCurrentStore, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useDocumentTitle();

  if (!user) return null;

  const isWeloze = user.role === UserRole.WELOZE_ADMIN || user.role === UserRole.WELOZE_VIEWER;
  const multiStore = user.stores.length > 1;

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-dvh bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur print:hidden">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          {/* Marca do weloze.lab à esquerda, nome da aplicação à direita do
              divisor. No celular o wordmark sai e sobra o símbolo + o nome —
              saber em qual sistema se está vale mais que a assinatura. */}
          <Link to="/" className="flex min-w-0 items-center gap-2.5">
            <BrandMark />
            <Wordmark className="hidden text-lg sm:inline-flex" />
          </Link>

          <span className="hidden h-6 w-px shrink-0 bg-slate-200 sm:block" aria-hidden />

          {/* Rótulo, não link. A navegação mora na faixa de baixo — aqui em
              cima fica só identidade, e por isso o texto não tem afordância de
              clique nenhuma. */}
          <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{APP_NAME}</span>

          <div className="ml-auto flex items-center gap-2">
            {multiStore && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  aria-expanded={open}
                  aria-haspopup="listbox"
                  className={cn(
                    'flex max-w-[15rem] items-center gap-2 rounded-lg border border-slate-300 bg-white',
                    'px-3 py-2 text-sm text-slate-700 shadow-sm transition-colors hover:bg-slate-50',
                  )}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  <span className="truncate">{currentStore?.companyName ?? 'Selecione'}</span>
                  <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                    {currentStore?.protheusRef}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                </button>

                {open && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
                    <ul
                      role="listbox"
                      className="absolute right-0 z-20 mt-1.5 max-h-80 w-80 overflow-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg"
                    >
                      {user.stores.map((store) => (
                        <li key={store.storeId}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={store.storeId === currentStore?.storeId}
                            onClick={() => {
                              setCurrentStore(store);
                              setOpen(false);
                            }}
                            className={cn(
                              'flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                              store.storeId === currentStore?.storeId
                                ? 'bg-brand-50 text-brand-800'
                                : 'text-slate-700 hover:bg-slate-50',
                            )}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">{store.companyName}</span>
                              <span className="block truncate text-xs text-slate-500">
                                {store.storeName}
                                {store.city && ` · ${store.city}/${store.state}`}
                              </span>
                            </span>
                            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                              {store.protheusRef}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight text-slate-800">{user.name}</p>
              <p className="text-xs leading-tight text-slate-500">
                {isWeloze ? 'Weloze' : (currentStore?.protheusRef ?? user.email)}
              </p>
            </div>

            <Button variant="ghost" size="sm" onClick={handleLogout} title="Sair">
              <LogOut className="h-4 w-4" aria-hidden />
              <span className="sr-only sm:not-sr-only">Sair</span>
            </Button>
          </div>
        </div>

        <SectionNav isWeloze={isWeloze} isAdmin={user.role === UserRole.WELOZE_ADMIN} />
      </header>

      {user.mustChangePassword && (
        <div className="border-b border-amber-200 bg-amber-50">
          <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2.5 text-sm text-amber-900 sm:px-6">
            <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
            <span>Sua senha é provisória.</span>
            <Link to="/perfil/senha" className="font-medium underline underline-offset-2">
              Defina uma nova agora
            </Link>
          </div>
        </div>
      )}

      <Breadcrumbs />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">{children}</main>
    </div>
  );
}
